import React, { useEffect, useState } from "react";
import { useRazorpay } from "../../../../context/RazorpayContext";
import formatDate from "../../../../common/DateFormate";
import { applyCoupon, removeCoupon } from "../../../../services/billing";

const PackageSummaryModal = ({ packageType, currentPlan, refetchBilling, showCoupen }) => {
  const {
    invoiceModalData,
    onClosePackageSummaryModal,
    handleCreatePlan
  } = useRazorpay();

  const [isLoading, setIsLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [removeCouponMsg, setRemoveCouponMsg] = useState("");
  const [couponError, setCouponError] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [showCouponField, setShowCouponField] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("upi"); // default
  const [isUpiLoading, setIsUpiLoading] = useState(false);

  // Load applied coupon from sessionStorage on mount
  useEffect(() => {
    const couponData = invoiceModalData?.invoiceDetails?.coupon;
    if (couponData && typeof couponData === "object" && Object.keys(couponData).length > 0) {

      // Set applied coupon state from API data
      setAppliedCoupon({
        code: couponData.code,
        trialCycle: couponData.trial_cycle,
        startTime: couponData.start_time,
        endTime: couponData.end_time,
        billingCycle: invoiceModalData.invoiceDetails.billing_cycle,
        totalAmount: invoiceModalData.invoiceDetails.total_amount,
        proratedAmount: invoiceModalData.invoiceDetails.prorated_amount,
        accounts: invoiceModalData.invoiceDetails.accounts,
        currency: invoiceModalData.invoiceDetails.currency
      });

      // Set the coupon code in the input field
      setCouponCode(couponData.code);
      setShowCouponField(true); // Auto-expand to show applied coupon
    } else {
      // Clear coupon if not present in API response
      setAppliedCoupon(null);
      setCouponCode('');
    }
  }, [invoiceModalData?.invoiceDetails?.coupon]);


  if (!invoiceModalData?.isInvoiceDetailsModal) return null;



  const handleCreatePlanArgument = (e) => {
    handleCreatePlan(
      e,
      setIsLoading,
      ((invoiceModalData?.planType === 'paymentMethod') ? ('generatePaymentUrl') : (currentPlan?.subscription?.status === "suspended" ? 'generatePaymentUrl' : packageType)),
      currentPlan,
      packageType === 'upgrade' && refetchBilling,
      paymentMethod
    );
  };

  const handlePaymentChange = async (e) => {
    const selectedMethod = e.target.value;
    setPaymentMethod(selectedMethod);
  };


  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    setIsApplying(true);
    setCouponError('');

    try {
      const response = await applyCoupon({
        coupon_code: couponCode.trim().toUpperCase(),
        package_id: invoiceModalData.invoiceDetails.accounts[0].id
      });
      const responseData = response?.data;

      if (responseData?.success) {
        // Store the coupon details and updated billing data
        const couponData = {
          code: responseData.data.coupon.code,
          trialCycle: responseData.data.coupon.trial_cycle,
          startTime: responseData.data.coupon.start_time,
          endTime: responseData.data.coupon.end_time,
          billingCycle: responseData.data.billing_cycle,
          totalAmount: responseData.data.total_amount,
          proratedAmount: responseData.data.prorated_amount,
          accounts: responseData.data.accounts,
          currency: responseData.data.currency
        };

        setAppliedCoupon(couponData);
        setCouponError('');
        setRemoveCouponMsg('');

      }
      else {
        setCouponError(responseData?.message || "Invalid coupon code");
        setAppliedCoupon(null);
        setRemoveCouponMsg('');
      }
    } catch (error) {
      setCouponError(error.response?.data?.message || "Failed to apply coupon");
      setAppliedCoupon(null);
      setRemoveCouponMsg('')
    } finally {
      setIsApplying(false);
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      const response = await removeCoupon({ user_id: invoiceModalData.invoiceDetails.accounts[0].id })
      setRemoveCouponMsg(response.data.message)
      setAppliedCoupon(null);
      setCouponCode('');
      setCouponError('');
    }
    catch (error) {
      setCouponError(error.response?.data?.message || "Failed to apply coupon");
      setAppliedCoupon(null);
      setRemoveCouponMsg('')
    }
  };

  const isCouponAllowed = invoiceModalData?.invoiceDetails?.accounts?.some(
    (item) => Number(item.amount.replace(/[₹,]/g, '')) > 0
  );

  // Use coupon data if applied, otherwise use original invoice data
  const displayAccounts = appliedCoupon?.accounts || invoiceModalData?.invoiceDetails?.accounts;
  const displayTotalAmount = appliedCoupon?.totalAmount || invoiceModalData?.invoiceDetails?.total_amount;
  const displayProratedAmount = appliedCoupon?.proratedAmount || invoiceModalData?.invoiceDetails?.prorated_amount;
  const displayBillingCycle = appliedCoupon?.billingCycle || invoiceModalData?.invoiceDetails?.billing_cycle;
  return (
    <div
      className="modal d-flex align-items-center justify-content-center"
      style={{
        display: "flex",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(8px)",
        zIndex: 1052,
        overflowY: "auto",
        padding: "20px",
      }}
    >
      <div
        className="modal-dialog animate__animated animate__fadeInDown"
        style={{
          maxWidth: "550px",
          width: "100%",
          margin: "auto",
        }}
      >
        <div
          className="modal-content border-0 shadow-lg"
          style={{
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            className="modal-header border-0 pb-2"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "24px 24px 20px"
            }}
          >
            <h4 className="modal-title fw-bold text-white m-0" style={{ fontSize: "1.25rem" }}>
              <i className="bi bi-receipt me-2"></i>
              Billing Summary
            </h4>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClosePackageSummaryModal}
              disabled={isLoading}
              style={{ opacity: 0.9 }}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body package-modal p-4" style={{ background: "var(--white-color)" }}>
            {invoiceModalData?.planType !== 'paymentMethod' && (
              <>
            {/* Billing Cycle Card */}
            <div className="bg-white rounded-3 p-3 mb-3 shadow-sm">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small fw-medium">
                  <i className="bi bi-calendar-check me-2 text-primary"></i>
                  Billing Cycle
                </span>
                <span className="fw-semibold text-dark">
                  {formatDate(displayBillingCycle) || ""}
                </span>
              </div>
            </div>

            {/* Accounts Table */}
            <div className="bg-white rounded-3 shadow-sm overflow-hidden mb-3">
              <table className="table mb-0">
                <thead style={{ background: "#f1f3f5" }}>
                  <tr>
                    <th className="px-3 pt-3 small fw-semibold text-secondary border-0">
                      Account Name
                    </th>
                    <th className="px-3 pt-3 small fw-semibold text-secondary text-end border-0">
                      Monthly
                    </th>
                    <th className="px-3 pt-3 small fw-semibold text-secondary text-end border-0">
                      This Month
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {displayAccounts?.map((item, index) => (
                    <tr key={index} style={{ borderBottom: "1px solid #e9ecef" }}>
                      <td className="px-3 pt-1 small text-dark">{item.name}</td>
                      <td className="px-3 pt-1 small text-end fw-medium text-dark">
                        {item.amount}
                      </td>
                      <td className="px-3 pt-1 small text-end fw-medium text-dark">
                        {item.prorated_amount}
                      </td>
                    </tr>
                  ))}

                  {/* Total Amount Row */}
                  <tr className="table-hover border-top">
                    <td className="px-3 py-2 small text-dark fw-semibold">Total Amount</td>
                    <td className="px-3 py-2 small text-end fw-bold text-dark">{displayTotalAmount}</td>
                    <td className="px-3 py-2 small text-end fw-bold text-dark">{displayProratedAmount}</td>
                  </tr>
                </tbody>
              </table>
            </div>



            {/* Coupon Section */}
            {(isCouponAllowed && showCoupen) && (
              <>
                <div className="bg-white rounded-3 p-3 shadow-sm">
                  <div
                    className="d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => !appliedCoupon && setShowCouponField(!showCouponField)}
                  >
                    <p className="d-block small fw-semibold text-dark mb-0">
                      <i className="bi bi-tag me-2 text-success"></i>
                      Have a coupon code?
                    </p>
                    {!appliedCoupon && (
                      <i className={`bi bi-chevron-${showCouponField ? 'up' : 'down'} text-muted`}></i>
                    )}
                  </div>

                  {/* Coupon Input Field */}
                  {showCouponField && !appliedCoupon && (
                    <div className="mt-3">
                      <div className="d-flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          onKeyPress={(e) => e.key === "Enter" && handleApplyCoupon()}
                          placeholder="ENTER CODE"
                          className="form-control flex-grow-1"
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #dee2e6",
                            padding: "10px 14px"
                          }}
                        />
                        <button
                          onClick={handleApplyCoupon}
                          disabled={!couponCode.trim() || isApplying}
                          className="btn px-4 py-2 fw-medium"
                          style={{
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                            borderRadius: "8px",
                            border: "none",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {isApplying ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                              Applying...
                            </>
                          ) : "Apply"}
                        </button>
                      </div>

                      {removeCouponMsg && (
                        <div className="alert alert-success d-flex align-items-center gap-2 mt-2 mb-0 py-2" style={{ fontSize: "0.85rem" }}>
                          <i className="bi bi-exclamation-triangle-fill"></i>
                          <span>{removeCouponMsg}</span>
                        </div>
                      )}

                      {/* Error Message */}
                      {couponError && (
                        <div className="alert alert-danger d-flex align-items-center gap-2 mt-2 mb-0 py-2" style={{ fontSize: "0.85rem" }}>
                          <i className="bi bi-exclamation-triangle-fill"></i>
                          <span>{couponError}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Applied Coupon Display */}
                  {appliedCoupon && (
                    <div className="mt-3">
                      <div className="p-3 rounded-3" style={{ background: "#d1f4e0", border: "1px solid #a3e7c5" }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <i className="bi bi-check-circle-fill text-success"></i>
                              <span className="fw-bold text-success">{appliedCoupon.code}</span>
                            </div>
                            {/* <p className="text-success small mb-0">
                            Free trial period activated
                          </p> */}
                            <div className=" d-flex align-items-start gap-2" style={{ fontSize: "0.85rem" }}>
                              <i className="bi bi-gift-fill"></i>
                              <div>
                                <div className="fw-semibold">Free Trial Applied!</div>
                                <div>Trial Period: {formatDate(appliedCoupon.trialCycle)}</div>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={handleRemoveCoupon}
                            className="btn btn-sm p-0"
                            style={{ color: "#198754" }}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </div>
                      </div>

                      {/* Info Message */}
                      <div className="d-flex align-items-start gap-2 mt-3 mb-0 py-2" style={{ fontSize: "0.85rem" }}>
                        <i className="bi bi-info-circle-fill"></i>
                        <span>Amount will be auto-debit after your free trial ends on {formatDate(appliedCoupon.trialCycle?.split(' - ')[1]) || 'trial end date'}.</span>
                      </div>
                    </div>
                  )}
                </div>

              </>
            )}
              </>
            )}

            {isCouponAllowed && (

              // {/* Payment Method Selection */}
              <div className="bg-white rounded-3 p-3 shadow-sm mb-3 mt-3">
                <p className="small fw-semibold text-dark mb-2">
                  Select Payment Method
                </p>

                <div className="d-flex gap-4">
                  {/* UPI */}
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="paymentMethod"
                      value="upi"
                      id="payment-upi"
                      checked={paymentMethod === "upi"}
                      onChange={handlePaymentChange}
                    // disabled={isUpiLoading}
                    />
                    <label
                      htmlFor="payment-upi"
                      className="form-check-label fw-medium">
                      UPI
                    </label>
                  </div>

                  {/* Card */}
                  <div className="form-check">
                    <input
                      id="payment-card"
                      className="form-check-input"
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={paymentMethod === "card"}
                      onChange={handlePaymentChange}
                    />
                    <label
                      htmlFor="payment-card" className="form-check-label fw-medium">
                      Card
                    </label>
                  </div>
                </div>

                {isUpiLoading && (
                  <div className="mt-2 small text-muted">
                    Initializing UPI payment...
                  </div>
                )}
              </div>
            )}

          </div>




          {/* Footer */}
          <div className="modal-footer border-0 p-4 pt-0" style={{ background: "var(--white-color)" }}>
            <div className="d-flex gap-3 w-100">
              <button
                className="btn btn-light flex-fill py-2 fw-medium"
                onClick={onClosePackageSummaryModal}
                disabled={isLoading}
                style={{
                  borderRadius: "10px",
                  border: "1px solid #dee2e6"
                }}
              >
                Cancel
              </button>
              <button
                className="btn flex-fill py-2"
                onClick={handleCreatePlanArgument}
                disabled={isLoading}
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  fontWeight:"600",
                  borderRadius: "10px",
                  border: "none"
                }}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    { appliedCoupon && invoiceModalData?.planType !== 'paymentMethod' ? 'Start Free Trial' : 'Continue to Payment'}
                    <i className="bi bi-arrow-right ms-2"></i>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageSummaryModal;